/* eslint-disable max-len */
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import * as ProjectsActions from '../actions/projects';
import * as ToastActions from '../actions/toast';
import * as SortingActions from '../actions/sorting';
import { Loader } from '../../App/components/Loader';
import ArrowUpIcon from '../../../images/sort-arrow-up.svg';
import ArrowDownIcon from '../../../images/sort-arrow-down.svg';
import SketchListRowBase from './SketchListRowBase';

const SketchList = ({
  user,
  getProjects,
  sketches,
  username,
  loading,
  sorting,
  toggleDirectionForField,
  resetSorting,
  mobile
}) => {
  const { t } = useTranslation();

  const searchTerm = useSelector((state) => state.search.sketchSearchTerm);

  useEffect(() => {
    resetSorting();
  }, [username, resetSorting]);

  // Fetch only once Firebase auth is CONFIRMED ready (authHydrated). onAuthStateChanged resolves
  // AFTER mount, and the optimistic cache sets user.id at mount — so without gating on authHydrated a
  // cold load runs while auth.currentUser is still null and the owner's Private sketches are dropped
  // (listProjects takes the non-owner branch). authHydrated flips false→true when auth resolves,
  // re-running this with a real session (or a confirmed signed-out state for public views).
  useEffect(() => {
    if (user.authHydrated) {
      getProjects(username);
    }
  }, [getProjects, username, user.id, user.authHydrated]);

  const getSketchesTitle = useMemo(
    () =>
      username === user.username
        ? t('SketchList.Title')
        : t('SketchList.AnothersTitle', { anotheruser: username }),
    [username, user.username, t]
  );

  const handleDeletedProject = useCallback(() => {
    getProjects(username);
  }, [getProjects, username]);

  // Client-side filter + sort over the flat `sketches` array.
  const displayedSketches = useMemo(() => {
    const { field, direction } = sorting;
    const term = searchTerm.trim().toLowerCase();

    const filtered = term
      ? sketches.filter((sketch) => sketch.name.toLowerCase().includes(term))
      : sketches;

    const sorted = [...filtered].sort((a, b) => {
      let comparison;
      if (field === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else {
        comparison =
          new Date(a[field]).getTime() - new Date(b[field]).getTime();
      }
      return direction === SortingActions.DIRECTION.ASC
        ? comparison
        : -comparison;
    });

    return sorted;
  }, [sketches, searchTerm, sorting]);

  // Show the spinner (not the empty state) while auth is still resolving — the fetch is gated on it —
  // and while a fetch is in flight with nothing to show yet.
  //
  // This used to read `loading && isInitialDataLoad`, where `isInitialDataLoad` was cleared by an
  // effect firing on `Array.isArray(sketches)`. But reducers/projects.js initialises `sketches` to
  // `[]` — already an array — so the effect ran on mount and that branch was unreachable: the
  // spinner was skipped and "No sketches" was painted over an in-flight fetch. Gating on the
  // rendered list instead means the populated table survives a refetch (e.g. after a delete) rather
  // than flashing away.
  const isLoading = () =>
    !user.authHydrated || (loading && displayedSketches.length === 0);

  const hasSketches = () => !isLoading() && displayedSketches.length > 0;

  const renderLoader = () => isLoading() && <Loader />;

  const renderEmptyTable = () => {
    if (!isLoading() && displayedSketches.length === 0) {
      return (
        <p className="sketches-table__empty">{t('SketchList.NoSketches')}</p>
      );
    }
    return null;
  };

  const getButtonLabel = useCallback(
    (fieldName, displayName) => {
      const { field, direction } = sorting;
      if (field !== fieldName) {
        return field === 'name'
          ? t('SketchList.ButtonLabelAscendingARIA', { displayName })
          : t('SketchList.ButtonLabelDescendingARIA', { displayName });
      }
      return direction === SortingActions.DIRECTION.ASC
        ? t('SketchList.ButtonLabelDescendingARIA', { displayName })
        : t('SketchList.ButtonLabelAscendingARIA', { displayName });
    },
    [sorting, t]
  );

  const renderFieldHeader = useCallback(
    (fieldName, displayName) => {
      const { field, direction } = sorting;
      const headerClass = classNames({
        'sketches-table__header': true,
        'sketches-table__header--selected': field === fieldName
      });
      const buttonLabel = getButtonLabel(fieldName, displayName);
      return (
        <th scope="col">
          <button
            className="sketch-list__sort-button"
            onClick={() => toggleDirectionForField(fieldName)}
            aria-label={buttonLabel}
          >
            <span className={headerClass}>{displayName}</span>
            {field === fieldName &&
              (direction === SortingActions.DIRECTION.ASC ? (
                <ArrowUpIcon
                  focusable="false"
                  role="img"
                  aria-label={t('SketchList.DirectionAscendingARIA')}
                />
              ) : (
                <ArrowDownIcon
                  focusable="false"
                  role="img"
                  aria-label={t('SketchList.DirectionDescendingARIA')}
                />
              ))}
          </button>
        </th>
      );
    },
    [sorting, getButtonLabel, toggleDirectionForField, t]
  );

  const userIsOwner = user.username === username;

  return (
    <>
      <article className="sketches-table-container">
        <Helmet>
          <title>{getSketchesTitle}</title>
        </Helmet>
        {renderLoader()}
        {renderEmptyTable()}
        {hasSketches() && (
          <table
            className="sketches-table"
            summary={t('SketchList.TableSummary')}
          >
            <thead>
              <tr>
                {renderFieldHeader('name', t('SketchList.HeaderName'))}
                {renderFieldHeader(
                  'createdAt',
                  t('SketchList.HeaderCreatedAt', {
                    context: mobile ? 'mobile' : ''
                  })
                )}
                {renderFieldHeader(
                  'updatedAt',
                  t('SketchList.HeaderUpdatedAt', {
                    context: mobile ? 'mobile' : ''
                  })
                )}
                {userIsOwner &&
                  renderFieldHeader('visibility', t('Visibility.Label'))}
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {displayedSketches.map((sketch) => (
                <SketchListRowBase
                  mobile={mobile}
                  key={sketch.id}
                  sketch={sketch}
                  user={user}
                  username={username}
                  onDeletedProject={handleDeletedProject}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        )}
      </article>
    </>
  );
};

SketchList.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
    authHydrated: PropTypes.bool,
    authenticated: PropTypes.bool.isRequired
  }).isRequired,
  getProjects: PropTypes.func.isRequired,
  sketches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      visibility: PropTypes.string
    })
  ).isRequired,
  username: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  toggleDirectionForField: PropTypes.func.isRequired,
  resetSorting: PropTypes.func.isRequired,
  sorting: PropTypes.shape({
    field: PropTypes.string.isRequired,
    direction: PropTypes.string.isRequired
  }).isRequired,
  mobile: PropTypes.bool
};

SketchList.defaultProps = {
  username: undefined,
  mobile: false
};

function mapStateToProps(state) {
  return {
    user: state.user,
    sketches: state.sketches ?? [],
    sorting: state.sorting,
    loading: state.loading,
    project: state.project
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    Object.assign({}, ProjectsActions, ToastActions, SortingActions),
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SketchList);
